import React, { createContext, useContext, useState } from 'react';
import css from './ExtraPackage.css';
import { FieldCheckbox, Tooltip } from '../../components';
import PackagesBenefitsModal from './PackagesBenefitsModal';
import classNames from 'classnames';
import { IconAnonymousL, IconBoost3xL, IconCrossL, IconEyeHideL, IconInfoM } from '../../icons';
import { extraPackagesConfig } from '../../marketplace-custom-config';

const ExtraPackageContext = createContext(undefined);

const useExtraPackageContext = () => {
    const context = useContext(ExtraPackageContext);

    if (!context) {
        throw new Error('useExtraPackageContext must be used within InterruptProcessModal.');
    }

    return context;
};

const Block = ({ children }) => {
    const { setBlockRef } = useExtraPackageContext();
    return (
        <div ref={setBlockRef} className={css.packagesWrapper}>
            {children}
        </div>
    );
};
const IconBoosting = () => (
    <div className={css.iconHolder}>
        <IconBoost3xL />
    </div>
);
const IconPrivateHorse = () => (
    <div className={css.iconHolder}>
        <IconEyeHideL />
    </div>
);
const IconAnonymHorse = () => (
    <div className={css.iconHolder}>
        <IconAnonymousL />
    </div>
);
const Category = ({ children }) => <p className={css.packagesCategoryHeading}>{children}</p>;
const UpperSection = ({ children }) => <div className={css.packagesUpperSection}>{children}</div>;

const Footer = ({ children }) => <footer className={css.footer}>{children}</footer>;
const Badge = ({ children, color = 'default' }) => (
    <div
        className={classNames(css.badge, {
            [css[color]]: !!css[color],
        })}
    >
        {children || 'Verfügbar'}
    </div>
);
const PackageTooltip = () => {
    const { id, blockRef } = useExtraPackageContext();
    const [tooltipVisible, setTooltipVisibility] = useState(false);

    const getStaticText = () =>
        `Das Aktivieren des Zusatzpakets ist kostenlos. Du benötigst ${extraPackagesConfig[id].price} Credits für jede Anfrage die Du sendest oder erhältst.`;

    if (!id || !extraPackagesConfig[id]) return;
    if (!blockRef) return;

    return (
        <Tooltip
            isStatic
            staticTooltipState={tooltipVisible}
            staticTooltipStateHandler={() => setTooltipVisibility(false)}
            parentContainerRef={{ current: blockRef }}
            tooltipClassName={css.tooltip}
            content={
                <>
                    <p>Hinweis</p>
                    <p>{getStaticText(id)}</p>
                    <IconCrossL
                        rootClassName={css.closeTooltip}
                        clickHandler={() => setTooltipVisibility(false)}
                    />
                </>
            }
        >
            <IconInfoM
                className={css.tooltipTarget}
                onClick={() => {
                    /**
                     * Fire scroll event to close any other tooltips
                     */
                    if (!tooltipVisible) {
                        window.scrollBy({ top: 1 });
                    }
                    setTimeout(() => {
                        setTooltipVisibility(prevState => !prevState);
                    }, 0);
                }}
            />
        </Tooltip>
    );
};
const TitleAndPrice = ({ title }) => {
    const { price } = useExtraPackageContext();
    return (
        <div>
            <h4 className={css.packagesTitle}>{title}</h4>

            <div className={css.packagesPricingInfo}>
                <span className={css.packagesPrice}>{price} Credits</span>
                <span className={css.packagesSubscriptionNote}>・Pro Anfrage</span>
                <PackageTooltip />
            </div>
        </div>
    );
};

const Checkbox = () => {
    const { disabled, id } = useExtraPackageContext();

    return (
        <FieldCheckbox
            type="checkbox"
            id={id}
            name={id}
            className={css.noPaddingCheckbox}
            togglerMode
            reversePosition
            disabled={disabled}
        />
    );
};

const Description = ({ children }) => {
    const { setModalCategory, id } = useExtraPackageContext();

    return (
        <p className={css.packagesDescription}>
            {children} <br />
            <span className={css.moreInfoLink} onClick={() => setModalCategory(id)}>
                Was ist enthalten?
            </span>
        </p>
    );
};

const ExtraPackage = ({ price, id, currentListing, disabled, children, isProfilePackageType }) => {
    const [modalCategory, setModalCategory] = useState(null);
    const [blockRef, setBlockRef] = useState(null);

    return (
        <ExtraPackageContext.Provider
            value={{ disabled, modalCategory, setModalCategory, price, id, blockRef, setBlockRef }}
        >
            {Boolean(modalCategory) && (
                <PackagesBenefitsModal
                    isOpen
                    category={modalCategory}
                    currentListing={currentListing}
                    isProfilePackageType={isProfilePackageType}
                    onClose={() => setModalCategory(null)}
                />
            )}
            {children}
        </ExtraPackageContext.Provider>
    );
};

ExtraPackage.IconBoosting = IconBoosting;
ExtraPackage.IconPrivateHorse = IconPrivateHorse;
ExtraPackage.IconAnonymHorse = IconAnonymHorse;
ExtraPackage.Category = Category;
ExtraPackage.UpperSection = UpperSection;
ExtraPackage.TitleAndPrice = TitleAndPrice;
ExtraPackage.Checkbox = Checkbox;
ExtraPackage.Description = Description;
ExtraPackage.Footer = Footer;
ExtraPackage.Badge = Badge;
ExtraPackage.Block = Block;

export default ExtraPackage;
